import crypto from 'crypto';
import os from 'os';
import { FusionQueryProvider } from '@devxdiscover/devhub-common';
import { ungzip } from 'pako';

/**
 * Allowed entity kinds
 */
export type AllowedEntityKinds =
  | 'component'
  | 'api'
  | 'template'
  | 'user'
  | 'group'
  | 'system';

/**
 * Creates an 8-character hash of the current environment
 * @private
 */
export function createEnvHash(): string {
  const envVars = process.env;

  const validVars = [
    'USER',
    'HOST',
    'HOSTNAME',
    'HOME',
    'SHELL',
    'TERM',
    'EDITOR',
    'LANG',
    'HOSTTYPE',
  ];
  const envKeys = Object.keys(envVars)
    .filter(x => validVars.includes(x))
    .sort();
  const hash = crypto.createHash('sha256');

  // Add some OS info to the hash
  hash.update(os.type());
  hash.update(os.arch());
  hash.update(os.hostname());
  hash.update(os.platform());
  hash.update(os.version());
  hash.update(os.release());

  // Add the env vars to the hash
  for (const key of envKeys) {
    const envValue = envVars[key];
    if (envValue) {
      hash.update(`${key}:${envValue}`);
    }
  }

  return hash.digest('hex').substring(0, 8);
}

/**
 * Dedupes an array of objects by a property. Returns a new array with the duplicates removed.
 * @param array The array to dedupe
 * @param property The property to dedupe by
 */
export function dedupe<T>(array: T[], property: (item: T) => any): T[] {
  const uniqueValues = new Set<any>();
  return array.filter(item => {
    const value = property(item);
    if (uniqueValues.has(value)) {
      return false;
    }
    uniqueValues.add(value);
    return true;
  });
}

/**
 * Paginates over a query
 * @param query - The query
 * @param variables - The variables
 * @param options - Additional options
 */
export async function paginateOverQuery<TResult, TVariables = {}>(
  query: string,
  variables: TVariables | {} = {},
  options: {
    queryProvider: FusionQueryProvider;
    haveMore: (result: TResult) => boolean;
    forEachPage: (result: TResult, offset?: number) => Promise<void>;
    stepSize?: number;
    onFailed?: (err: Error) => void;
    onCompleted?: () => void;
  },
): Promise<void> {
  let paginationExhausted = false;
  let offset = 0;
  let failed = false;
  const stepSize = options.stepSize ?? 100;
  while (!paginationExhausted) {
    try {
      const result = await options.queryProvider.query<TResult>(query, {
        ...variables,
        pagination: {
          limit: stepSize,
          offset: offset,
        },
      });

      await options.forEachPage(result.data, offset);
      if (!options.haveMore(result.data)) {
        paginationExhausted = true;
      } else {
        offset += stepSize;
      }
    } catch (err: any) {
      failed = true;
      if (options.onFailed) {
        options.onFailed(err);
      } else {
        throw err;
      }
      paginationExhausted = true;
    }
  }
  if (options.onCompleted && !failed) {
    options.onCompleted();
  }
}

/**
 * Paginates over a query
 * @param query - The query
 * @param variables - The variables
 * @param options - Additional options
 */
export async function* paginateOverQueryGenerator<TResult, TVariables = {}>(
  query: string,
  variables: TVariables | {} = {},
  options: {
    queryProvider: FusionQueryProvider;
    haveMore: (result: TResult) => boolean;
    stepSize?: number;
  },
): AsyncGenerator<TResult> {
  let paginationExhausted = false;
  let offset = 0;
  const stepSize = options.stepSize ?? 100;
  while (!paginationExhausted) {
    try {
      const result = await options.queryProvider.query<TResult>(query, {
        ...variables,
        pagination: {
          limit: stepSize,
          offset: offset,
        },
      });

      if (!options.haveMore(result.data)) {
        paginationExhausted = true;
      } else {
        yield result.data;
        offset += stepSize;
      }
    } catch (err: any) {
      paginationExhausted = true;
      throw err;
    }
  }
}

/**
 * Converts an org name to a namespace
 * @param orgName The org name
 */
export function orgNameToNamespace(orgName: string | undefined): string {
  if (!orgName) {
    return 'default';
  }
  return orgName
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .slice(0, 63);
}

/**
 * Checks if the given entity ref is valid.
 * @param ref The entity ref
 * @param allowedKinds The allowed kinds
 */
export function isValidEntityRef(
  ref: string,
  ...allowedKinds: AllowedEntityKinds[]
): boolean {
  let kinds: AllowedEntityKinds[] = allowedKinds;
  if (!kinds || kinds.length === 0) {
    kinds = ['component', 'api', 'template', 'user', 'group', 'system'];
  }
  const kindsPattern = kinds.join('|');
  const regex = new RegExp(`((${kindsPattern}):)*[a-z0-9-]+\/[a-z-_.]+`, 'i');
  return regex.test(ref);
}

/**
 * Creates a normalized entity reference or returns undefined if the ref is invalid.
 * @param ref The entity ref
 * @param defaultKind The default kind
 */
export function normalizeEntityRef(
  ref: string,
  defaultKind: AllowedEntityKinds,
): string | undefined {
  // Check if the ref is already normalized and ignore duplicate kinds with non-capturing group `(?:...)`
  // const testString = "group:group:example-group/group-name";
  // const result = ref.replace(hasKind, '$1:$2'); // expect "group:example-group/group-name"
  const hasKind = new RegExp(
    `(?:\\b(component|api|template|user|group|system):\\b\\s*)*([a-z0-9-]+\\/[a-z-_.]+)`,
    'i',
  );
  if (hasKind.test(ref)) {
    return ref.replace(hasKind, `$1:$2`).replace(/^:*/, '');
  }

  const hasValidNamespaceAndName = new RegExp(`[a-z0-9-]+\/[a-z-_.]+`, 'i');
  if (hasValidNamespaceAndName.test(ref)) {
    return `${defaultKind}:${ref}`;
  }
  return undefined;
}

/**
 * Gets the org for an SCM slug
 * @param scmSlug The SCM slug
 */
export function getOrgForScmSlug(
  scmSlug: string | undefined,
): string | undefined {
  return (scmSlug?.split('/') ?? [])?.[0];
}

/**
 * The name of the catch-all group
 */
export const CATCH_ALL_GROUP_NAME = 'all-members';

/**
 * Decodes a base64 string that is gzipped
 * @param base64String The base64 string
 */
export function decodeBase64GZip(base64String?: string): string | undefined {
  if (!base64String || base64String.length === 0) {
    return undefined;
  }
  const buffer = Buffer.from(base64String, 'base64');
  const decompressedData = ungzip(buffer);
  return new TextDecoder('utf-8').decode(decompressedData);
}

/**
 * The default catalog metadata filename
 */
export const DEFAULT_CATALOG_METADATA_FILENAME = '.devhub.yml';

/**
 * Enum for the different features
 */
export enum Feature {
  ACTION_CENTER = 'actionCenter',
  AD_GROUP_DELETE = 'adGroupDelete',
  AI_INVENTORY = 'aiInventory',
  API_PORTAL = 'apiPortal',
  API_PORTAL_CORS = 'apiPortalCors',
  APP_BULK_EDIT = 'appBulkEdit',
  APP_DECOM_WORKFLOW = 'appDecomWorkflow',
  APPLICATIONS = 'applications',
  BUSINESS_CAPABILITIES = 'businessCapabilities',
  ARTIFACTORY_SELF_SERVICE = 'artifactorySelfService',
  CATALOG = 'catalog',
  CGA_HIERARCHY_REPORT = 'cgaHierarchyReport',
  CHANNEL_CREDENTIALS = 'channelCredentials',
  CHEATSHEET = 'cheatsheet',
  CLOUD_ACCOUNTS = 'cloudAccounts',
  CLOUD_FOUNDRY = 'cloudFoundry',
  CNAP_TENANCY = 'cnapTenancy',
  COMMUNITY = 'community',
  CONNECTIONS = 'connections',
  CORPORATE_ENTITY = 'corporateEntity',
  DBO_MANAGEMENT = 'dboManagement',
  DEPENDENCY_DIAGRAM = 'dependencyDiagram',
  DEVHUB_PUBLIC_HOMEPAGE = 'devhubPublicHomepage',
  DEVXBOT = 'devxbot',
  DEVXPROTECT_EXPERIENCE = 'devxprotectExperience',
  DOCUMENTATION = 'documentation',
  DPX_WORKFLOW_SIDEBAR = 'dpxWorkflowSidebar',
  EMP_LEVEL_CHECK = 'empLevelCheck',
  EXECUTIVE_PERSONA = 'executivePersona',
  EXPERIMENTATION_ONBOARDING = 'experimentationOnboarding',
  EXPERIMENTATION_ONBOARDING_ADGROUPS = 'experimentationOnboardingAdGroups',
  EXPERIMENTATION_ONBOARDING_APPLICATION = 'experimentationOnboardingApplication',
  EXPERIMENTATION_ONBOARDING_CNAPTENANCY = 'experimentationOnboardingCnapTenancy',
  EXPERIMENTATION_ONBOARDING_GITHUBREPO = 'experimentationOnboardingGitHubRepo',
  FEEDBACK = 'feedback',
  FLOWHUB = 'flowhub',
  GITHUB_ENTERPRISE = 'githubEnterprise',
  GITHUB_ENTERPRISE_CLOUD = 'ghec',
  GREEN_SCORE = 'greenScore',
  IAM = 'iam',
  MARKETPLACE = 'marketplace',
  NEW_MARKETPLACE = 'newMarketplace',
  NOTIFICATIONS = 'notifications',
  NOVA = 'nova',
  ONBOARDING = 'onboarding',
  ONDEMAND_REFRESH = 'ondemandRefresh',
  ONDEMAND_REFRESH_USER = 'ondemandRefreshUser',
  PLUGIN_SHIELD = 'pluginShield',
  PRIVACY_PLUGIN = 'privacyPlugin',
  PRIVACY_SURVEY = 'privacySurvey',
  RESOURCE_REPORT = 'resourceReport',
  RESOURCETAB = 'resourceTab',
  RESOURCE_DATABASE_TAB = 'resourceDatabaseTab',
  SECURITY_VULNERABILITIES = 'securityVulnerabilities',
  SHIELD_ROUTER = 'shieldRouter',
  SHOWBACK = 'showback',
  SIDEBAR_SEARCH = 'sidebarSearch',
  SIGNALS = 'signals',
  SLACK_NOTIFICATIONS = 'slackNotifications',
  SNYK_ONBOARDING = 'snykOnboarding',
  SOLUTIONS = 'solutions',
  SONARQUBE = 'sonarqube',
  UNIVERSAL_WORKLIST = 'universalWorklist',
  USER_ROLES = 'userRoles',
  XPKI_ROUTER = 'xpkiRouter',
}

/**
 * Returns a promise that resolves after the given number of milliseconds.
 * @param ms The number of milliseconds to wait
 */
export function delay(ms: number): Promise<void> {
  return new Promise<void>(resolve => setTimeout(resolve, ms));
}

/**
 * Checks if the given URL is a relative URL.
 * @param paramUrl The URL to check
 */
export function isRelativeUrl(paramUrl: string): boolean {
  return (
    !paramUrl.startsWith('http://') &&
    !paramUrl.startsWith('https://') &&
    !paramUrl.startsWith('//') && // Exclude tel URLs
    !paramUrl.includes(':') && // Exclude URLs with schemes
    paramUrl.startsWith('/')
  );
}
